import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Button, 
  Tooltip, 
  Paper,
  alpha,
  useTheme,
  Avatar,
  Chip,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  CalendarToday as CalendarTodayIcon,
  Tour as TourIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useBudget } from '../../contexts/BudgetContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import MonthPicker from './MonthPicker';

const DashboardHeader: React.FC = () => {
  const { 
    currentBudget, 
    canNavigateNext, 
    canNavigatePrevious, 
    handlePreviousMonth, 
    handleNextMonth,
    availableMonths,
    selectedDate,
    setSelectedDate
  } = useBudget();
  const { changeHistory, undoneChanges, handleUndo, handleRedo, updatingTransaction } = useNavigation();
  const { startTour } = useOnboarding();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);

  const currentDate = currentBudget ? new Date(currentBudget.month) : new Date();
  const formattedDate = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const isCurrentMonth = new Date().getMonth() === currentDate.getMonth() && 
                         new Date().getFullYear() === currentDate.getFullYear();

  const handleOpenMonthPicker = () => {
    setIsMonthPickerOpen(true);
  };

  const handleCloseMonthPicker = () => {
    setIsMonthPickerOpen(false);
  };

  const handleMonthSelect = (month: Date) => {
    setSelectedDate(month);
  };

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Open month picker with 'M' key
      if (event.key.toLowerCase() === 'm' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // Don't trigger if user is typing in an input field
        if ((event.target as HTMLElement).tagName === 'INPUT' || 
            (event.target as HTMLElement).tagName === 'TEXTAREA' ||
            (event.target as HTMLElement).contentEditable === 'true') {
          return;
        }
        event.preventDefault();
        setIsMonthPickerOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: { xs: 2, sm: 3 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.04)})`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }
      }}
    >
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={{ xs: 3, md: 2 }}
      >
        {/* Left Section - Month Navigation */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent={{ xs: 'center', md: 'flex-start' }}
          gap={{ xs: 1, sm: 2 }}
        >
          {!isSmall && (
            <Tooltip title="Select Month (Press M)">
              <Avatar 
                onClick={handleOpenMonthPicker}
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.25),
                    transform: 'scale(1.05)',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }
                }}
              >
                <CalendarTodayIcon fontSize={isSmall ? 'small' : 'medium'} />
              </Avatar>
            </Tooltip>
          )}
          
          <Box display="flex" alignItems="center" gap={1}>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title={!canNavigatePrevious ? "No earlier data available" : "Previous month"}>
                <span>
                  <IconButton 
                    onClick={handlePreviousMonth} 
                    size={isMobile ? "small" : "medium"}
                    disabled={!canNavigatePrevious}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      minWidth: { xs: 40, sm: 44 },
                      minHeight: { xs: 40, sm: 44 },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        transform: 'translateX(-2px)',
                      },
                      '&:disabled': {
                        bgcolor: alpha(theme.palette.grey[400], 0.08),
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ChevronLeftIcon fontSize={isMobile ? 'small' : 'medium'} />
                  </IconButton>
                </span>
              </Tooltip>
            </motion.div>
            
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              mx={{ xs: 1, sm: 2 }}
              minWidth={{ xs: '200px', sm: '250px' }}
              onClick={handleOpenMonthPicker}
              sx={{
                cursor: 'pointer',
                borderRadius: 2,
                padding: { xs: 1, sm: 1.5 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateY(-1px)',
                }
              }}
            >
              <Tooltip title="Click to select a different month (Press M)">
                <Typography 
                  variant={isSmall ? "h5" : isMobile ? "h4" : "h4"} 
                  component="h1" 
                  fontWeight={700} 
                  color="primary.main"
                  textAlign="center"
                  sx={{ 
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                    lineHeight: 1.2
                  }}
                >
                  {formattedDate}
                </Typography>
              </Tooltip>
              {isCurrentMonth && (
                <Chip
                  label="Current Month"
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ 
                    mt: 0.5,
                    fontSize: { xs: '0.675rem', sm: '0.75rem' },
                    height: { xs: 20, sm: 24 }
                  }}
                />
              )}
            </Box>
            
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title={!canNavigateNext ? "Cannot navigate to future months" : "Next month"}>
                <span>
                  <IconButton 
                    onClick={handleNextMonth} 
                    size={isMobile ? "small" : "medium"}
                    disabled={!canNavigateNext}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      minWidth: { xs: 40, sm: 44 },
                      minHeight: { xs: 40, sm: 44 },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        transform: 'translateX(2px)',
                      },
                      '&:disabled': {
                        bgcolor: alpha(theme.palette.grey[400], 0.08),
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ChevronRightIcon fontSize={isMobile ? 'small' : 'medium'} />
                  </IconButton>
                </span>
              </Tooltip>
            </motion.div>
          </Box>
        </Box>

        {/* Right Section - Tour and Undo/Redo Actions */}
        <Stack 
          direction={{ xs: 'row', md: 'row' }} 
          spacing={1}
          justifyContent={{ xs: 'center', md: 'flex-end' }}
          alignItems="center"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Take a tour of the dashboard features">
              <Button
                onClick={startTour}
                startIcon={<TourIcon fontSize={isMobile ? 'small' : 'medium'} />}
                size={isMobile ? "small" : "medium"}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  color: theme.palette.info.main,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minHeight: { xs: 36, sm: 40 },
                  px: { xs: 1.5, sm: 2 },
                  '&:hover': {
                    borderColor: theme.palette.info.main,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    boxShadow: `0 2px 8px 0 ${alpha(theme.palette.info.main, 0.2)}`,
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {isSmall ? '' : 'Tour'}
              </Button>
            </Tooltip>
          </motion.div>

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title={changeHistory.length === 0 ? "No changes to undo" : "Undo last change"}>
              <span>
                <Button
                  onClick={handleUndo}
                  startIcon={<UndoIcon fontSize={isMobile ? 'small' : 'medium'} />}
                  size={isMobile ? "small" : "medium"}
                  variant="outlined"
                  disabled={changeHistory.length === 0 || updatingTransaction}
                  sx={{
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    minHeight: { xs: 36, sm: 40 },
                    px: { xs: 1.5, sm: 2 },
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    '&:disabled': {
                      borderColor: alpha(theme.palette.grey[400], 0.3),
                      color: theme.palette.grey[400],
                      bgcolor: alpha(theme.palette.grey[400], 0.05),
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSmall ? '' : 'Undo'}
                  {changeHistory.length > 0 && !isSmall && (
                    <Chip
                      label={changeHistory.length}
                      size="small"
                      sx={{ 
                        ml: 1, 
                        height: { xs: 16, sm: 20 }, 
                        fontSize: { xs: '0.675rem', sm: '0.75rem' },
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main
                      }}
                    />
                  )}
                </Button>
              </span>
            </Tooltip>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title={undoneChanges.length === 0 ? "No changes to redo" : "Redo last change"}>
              <span>
                <Button
                  onClick={handleRedo}
                  startIcon={<RedoIcon fontSize={isMobile ? 'small' : 'medium'} />}
                  size={isMobile ? "small" : "medium"}
                  variant="outlined"
                  disabled={undoneChanges.length === 0 || updatingTransaction}
                  sx={{
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.secondary.main, 0.3),
                    color: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    minHeight: { xs: 36, sm: 40 },
                    px: { xs: 1.5, sm: 2 },
                    '&:hover': {
                      borderColor: theme.palette.secondary.main,
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      boxShadow: `0 2px 8px 0 ${alpha(theme.palette.secondary.main, 0.2)}`,
                    },
                    '&:disabled': {
                      borderColor: alpha(theme.palette.grey[400], 0.3),
                      color: theme.palette.grey[400],
                      bgcolor: alpha(theme.palette.grey[400], 0.05),
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSmall ? '' : 'Redo'}
                  {undoneChanges.length > 0 && !isSmall && (
                    <Chip
                      label={undoneChanges.length}
                      size="small"
                      sx={{ 
                        ml: 1, 
                        height: { xs: 16, sm: 20 }, 
                        fontSize: { xs: '0.675rem', sm: '0.75rem' },
                        bgcolor: alpha(theme.palette.secondary.main, 0.15),
                        color: theme.palette.secondary.main
                      }}
                    />
                  )}
                </Button>
              </span>
            </Tooltip>
          </motion.div>
        </Stack>
      </Box>
      
      {/* Month Picker Dialog */}
      <MonthPicker
        open={isMonthPickerOpen}
        onClose={handleCloseMonthPicker}
        availableMonths={availableMonths}
        currentMonth={selectedDate}
        onMonthSelect={handleMonthSelect}
      />
    </Paper>
  );
};

export default DashboardHeader; 